/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { FirebaseError } from "firebase/app";
import { getAuth } from "firebase/auth";
import { BACKEND_URL } from "../../constants";
import { repeatParamsSerialiser } from "./serialiser";
import type { RootState } from "store/store";

const baseQuery = fetchBaseQuery({
  baseUrl: BACKEND_URL,
  paramsSerializer: repeatParamsSerialiser,
  prepareHeaders: async (headers, { getState }) => {
    const auth = getAuth();
    const idToken = await auth.currentUser?.getIdToken();
    if (idToken) {
      headers.set("Authorization", `Bearer ${idToken}`);
    }

    const state = getState() as RootState | undefined;
    const locale = state?.settings?.locale;
    if (locale) {
      const [lang, region] = locale.split("-");
      const primary = region ? `${lang ?? ""}-${region}` : lang;
      const base = lang?.toLowerCase();
      const acceptLanguage =
        base && region ? `${primary ?? ""},${base};q=0.9` : (primary ?? "");
      headers.set("Accept-Language", acceptLanguage);
    }

    return headers;
  },
});

const fetchBaseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const auth = getAuth();
  const result = await baseQuery(args, api, extraOptions);
  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 403)
  ) {
    await auth.currentUser?.getIdToken(true).catch((e: unknown) => {
      if (e instanceof FirebaseError && e.code === "auth/user-token-expired") {
        window.location.href = "/sign-in";
      }
    });
    return baseQuery(args, api, extraOptions);
  }
  return result;
};

// Endpoint definitions are injected into this shared API service.
export const templateApi = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQueryWithReAuth,
  endpoints: () => ({}),
});
